import { useState } from 'react'
import { PageHeader, Panel, Button, Banner, Field, Checkbox, DefinitionList, useToast } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { post } from '../../lib/api'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { TermsText } from './Signup'

export type ClientTerms = {
  accepted: { id: string; version: string; accepted_at: string; accepted_local: string; typed_name: string; title: string | null; email: string; business_name: string; text: string; source: string } | null
  update: { version_id: string; version: string; effective_at: string; in_effect: boolean; text: string; agree_label: string } | null
}

const day = (d: string) => new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Phoenix', month: 'long', day: 'numeric', year: 'numeric' })

/**
 * The Terms this business signed, word for word, with the record of signing
 * (Business Terms 16.4), and a newer version to accept when there is one (16.5).
 */
export default function ClientAgreement() {
  const t = useApi<ClientTerms>('/api/client/terms')
  if (t.error) return <ErrorBlock error={t.error} />
  if (!t.data) return <LoadingBlock />
  const { accepted, update } = t.data
  return (
    <>
      <PageHeader title="Your agreement" subtitle="The Handpicked Business Terms you signed, exactly as you saw them." />
      <div className="stack">
        {update && <UpdatePanel update={update} onDone={t.reload} />}
        {!accepted ? <Panel><p style={{ margin: 0 }}>Your agreement was signed on paper. Ask for a copy any time.</p></Panel> : (
          <>
            <Panel>
              <div className="panel-header"><h2 className="panel-title">Version {accepted.version}</h2><span className="pill is-good">{update && update.in_effect ? 'Earlier version' : 'In effect'}</span></div>
              <DefinitionList items={[
                { label: 'Signed by', value: `${accepted.typed_name}${accepted.title ? `, ${accepted.title}` : ''}` },
                { label: 'For', value: accepted.business_name },
                { label: 'When', value: accepted.accepted_local },
                { label: 'Email', value: accepted.email },
              ]} />
            </Panel>
            <Panel><TermsText text={accepted.text} /></Panel>
          </>
        )}
      </div>
    </>
  )
}

function UpdatePanel({ update, onDone }: { update: NonNullable<ClientTerms['update']>; onDone: () => void }) {
  const toast = useToast()
  const [agree, setAgree] = useState(false)
  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const sign = async () => {
    setBusy(true)
    try { await post('/api/client/terms/accept', { version_id: update.version_id, typed_name: name, title, agree }); toast('Signed. A copy is on its way to your email.', 'pos'); onDone() }
    catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Panel>
      <Banner tone="warn" title={`New Terms, version ${update.version}, ${update.in_effect ? 'in effect since' : 'from'} ${day(update.effective_at)}`}>
        Until you agree to them, the version you signed before applies to you (Business Terms 16.5).
      </Banner>
      <div style={{ marginTop: 'var(--s-5)' }}>
        <Button variant="secondary" onClick={() => setShow(!show)}>{show ? 'Hide the new Terms' : 'Read the new Terms'}</Button>
      </div>
      {show && <div className="terms-box" style={{ marginTop: 'var(--s-5)' }}><TermsText text={update.text} /></div>}
      <div className="stack" style={{ marginTop: 'var(--s-5)' }}>
        <Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)}
          label={<>I have read and agree to the updated <button type="button" className="link-button inline" onClick={() => setShow(true)}>Handpicked Business Terms</button></>} />
        <div className="form-grid">
          <Field label="Type your full name" id="u-name" value={name} onChange={(e) => setName(e.target.value)} />
          <Field label="Your title" id="u-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <p className="sign-line">By tapping Agree, you sign the updated Handpicked Business Terms with your typed name.</p>
        <div><Button onClick={sign} disabled={!agree || !/\S+\s+\S+/.test(name.trim()) || busy}>{busy ? 'Signing' : 'Agree'}</Button></div>
      </div>
    </Panel>
  )
}
